import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
    TextField, Paper, Button, Typography, CircularProgress, Divider,
    TableContainer, Table, TableHead, TableRow, TableCell, TableBody,
    Chip, Box, MenuItem, Tooltip, IconButton, TablePagination,
    InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions,
    Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DoneIcon from '@mui/icons-material/Done';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import apiService from '../../services/actions';
import { toast } from 'react-toastify';

const T = {
    primary: '#1976d2',
    primaryLight: '#e3f2fd',
    primaryMid: '#bbdefb',
    primaryDark: '#1565c0',
    secondary: '#9c27b0',
    success: '#2e7d32',
    successBg: '#e8f5e9',
    error: '#c62828',
    errorBg: '#ffebee',
    errorMid: '#ef9a9a',
    border: '#e0e0e0',
    rowHover: '#f0f7ff',
    rowAlt: '#fafafa',
    textPrimary: '#212121',
    textSecondary: '#616161',
    sectionBg: '#f8fafc',
    disabled: '#bdbdbd',
};

const DEFAULT_LAB_PARTNER = 'GENERAL_DIAGNOSIS';

const todayStr = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const formatDateTime = (d) => {
    if (!d) return '-';
    try {
        return new Date(d).toLocaleString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
    } catch {
        return d;
    }
};

function LabPartnerErrorLogList() {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialOrderId = queryParams.get('orderId') || location.state?.orderId || '';
    const initialLabPartner = queryParams.get('labPartner') || location.state?.labPartner || DEFAULT_LAB_PARTNER;
    const initialDate = queryParams.get('date') || location.state?.date || todayStr();

    const loginObjStr = localStorage.getItem('loginObj');
    const loginObj = JSON.parse(loginObjStr || 'null');
    const userCode = loginObj?.userSession?.userCode || localStorage.getItem('userCode') || localStorage.getItem('access_name') || 'CUREBAY ADMIN';

    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [retryingOrderId, setRetryingOrderId] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogData, setDialogData] = useState(null); // { orderId, failureLogId, labPartner }
    const [dialogRows, setDialogRows] = useState([]); // [{ tempId, orderDetailId, testName, codeType, oldTestCode, currentTestCode, newTestCode, reason }]
    const [dialogValidationErrors, setDialogValidationErrors] = useState(false);
    const [dialogSubmitting, setDialogSubmitting] = useState(false);

    const [labPartnerOptions, setLabPartnerOptions] = useState([initialLabPartner]);
    const [labPartner, setLabPartner] = useState(initialLabPartner);
    const [logDate, setLogDate] = useState(initialDate);
    const [orderId, setOrderId] = useState(initialOrderId);

    const [tableList, setTableList] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const getLabPartnerOptions = useCallback(async (date) => {
        try {
            const res = await apiService.getLabPartners(date);
            const options = res?.data?.data || [];
            if (options.length) {
                setLabPartnerOptions(options);
                setLabPartner((prev) => (options.includes(prev) ? prev : options[0]));
            } else {
                setLabPartnerOptions([DEFAULT_LAB_PARTNER]);
            }
        } catch (error) {
            console.error('Error fetching lab partners:', error);
            setLabPartnerOptions([DEFAULT_LAB_PARTNER]);
        }
    }, []);

    const getTableList = useCallback(async (partner = labPartner, date = logDate, ordId = orderId) => {
        const cleanOrdId = ordId ? String(ordId).trim() : '';
        setLoading(true);
        try {
            const filterPayload = {};
            if (partner) filterPayload.labPartner = partner;
            const targetDate = date || logDate;
            if (targetDate) filterPayload.date = targetDate;
            if (cleanOrdId) filterPayload.orderId = cleanOrdId;
            const res = await apiService.getLabPartnerErrorLogs(filterPayload);
            const data = res?.data?.data || [];
            setTableList(Array.isArray(data) ? data : []);
            setSelectedIds([]);
            setPage(0);
        } catch (error) {
            console.error('Error fetching error logs:', error);
            setTableList([]);
        }
        setLoading(false);
    }, [labPartner, logDate, orderId]);

    useEffect(() => {
        getLabPartnerOptions(initialDate);
        getTableList(initialLabPartner, initialDate, initialOrderId);
    }, []);

    const handleLabPartnerChange = (e) => {
        const v = e.target.value;
        setLabPartner(v);
        getTableList(v, logDate, orderId);
    };

    const handleDateChange = (e) => {
        const v = e.target.value;
        setLogDate(v);
        getLabPartnerOptions(v);
        getTableList(labPartner, v, orderId);
    };

    const handleSearchOrderId = () => {
        getTableList(labPartner, logDate, orderId);
    };

    const handleClearOrderId = () => {
        setOrderId('');
        getTableList(labPartner, logDate, '');
    };

    const handleRefresh = () => getTableList(labPartner, logDate, orderId);

    const toggleSelectAll = (e) => {
        const currentPageItems = getCurrentPageItems();
        const currentPageIds = currentPageItems.map((r) => r.id);
        if (e.target.checked) {
            setSelectedIds((prev) => {
                const newSelected = [...prev];
                currentPageIds.forEach(id => {
                    if (!newSelected.includes(id)) {
                        newSelected.push(id);
                    }
                });
                return newSelected;
            });
        } else {
            setSelectedIds((prev) => prev.filter(id => !currentPageIds.includes(id)));
        }
    };

    const toggleSelectRow = (id) => {
        setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    };

    const markHandled = async (ids) => {
        if (!ids.length) return;
        setUpdating(true);
        try {
            const res = await apiService.markLabPartnerErrorLogHandled({ ids, updatedBy: userCode });
            const resData = res?.data;
            if (resData?.status) {
                toast.success("Failure status updated successfully.");
            } else {
                toast.error('Failed to update.');
            }
            getTableList(labPartner, logDate, orderId);
        } catch (error) {
            console.error('Error marking handled:', error);
            toast.error('Something went wrong while updating.');
        }
        setUpdating(false);
    };

    const handleRetrigger = async (targetOrderId, row = null) => {
        if (!targetOrderId) {
            toast.error("Order ID not found.");
            return;
        }
        setRetryingOrderId(targetOrderId);
        try {
            // Step 1: Call api/labPartnerErrorLog/list for this order
            const partner = row?.labPartner || labPartner || 'PREDLAB';
            const rowDate = row?.createdDate ? String(row.createdDate).split('T')[0] : logDate;

            let logRes = await apiService.getLabPartnerErrorLogs({
                labPartner: partner,
                orderId: String(targetOrderId).trim(),
                date: rowDate,
            });

            let logItems = logRes?.data?.data || [];
            // If empty with date constraint, fallback to querying without date constraint
            if (!Array.isArray(logItems) || logItems.length === 0) {
                logRes = await apiService.getLabPartnerErrorLogs({
                    labPartner: partner,
                    orderId: String(targetOrderId).trim(),
                });
                logItems = logRes?.data?.data || [];
            }

            // Find the failure log item for this order
            const logItem =
                (Array.isArray(logItems) && logItems.find((it) => String(it.orderId) === String(targetOrderId) && String(it.status).toUpperCase() === 'FAILURE')) ||
                (Array.isArray(logItems) && logItems.find((it) => String(it.orderId) === String(targetOrderId))) ||
                (Array.isArray(logItems) && logItems[0]) ||
                row;

            let predlabFailure = logItem?.predlabFailure;
            if (typeof predlabFailure === 'string') {
                try { predlabFailure = JSON.parse(predlabFailure); } catch (e) {}
            }

            // Check if retry is blocked by partner
            if (predlabFailure?.retryBlocked) {
                toast.error(predlabFailure.blockingReason || "Retry is currently blocked for this order.");
                setRetryingOrderId(null);
                return;
            }

            const unmappedTests = predlabFailure?.unmappedTests || logItem?.unmappedTests || [];

            // If response has unmappedTests with length > 0, open popup with orderDetails
            if (Array.isArray(unmappedTests) && unmappedTests.length > 0) {
                const rows = [];
                unmappedTests.forEach((ut, uIdx) => {
                    const details = Array.isArray(ut.orderDetails) && ut.orderDetails.length > 0
                        ? ut.orderDetails
                        : [ut];

                    details.forEach((od, odIdx) => {
                        rows.push({
                            tempId: `${ut.testID || uIdx}-${od.orderDetailId || odIdx}`,
                            orderDetailId: od.orderDetailId ?? null,
                            testName: od.testName || ut.testName || `Test ${od.currentTestCode || ut.testID || uIdx + 1}`,
                            codeType: od.codeType || ut.codeType || 'TEST',
                            oldTestCode: String(od.currentTestCode ?? od.labTestCode ?? ut.testID ?? ut.testCode ?? ''),
                            currentTestCode: String(od.currentTestCode ?? od.labTestCode ?? ut.testID ?? ut.testCode ?? ''),
                            newTestCode: '',
                            reason: '',
                        });
                    });
                });

                if (rows.length > 0) {
                    setDialogData({
                        orderId: targetOrderId,
                        failureLogId: logItem?.id || row?.id || null,
                        labPartner: partner,
                    });
                    setDialogRows(rows);
                    setDialogValidationErrors(false);
                    setDialogOpen(true);
                    setRetryingOrderId(null);
                    return;
                }
            }

            // Step 2 (Normal Retry): If no unmapped tests, directly call retry-booking
            const payload = {
                orderId: String(targetOrderId),
                triggeredBy: userCode,
                ...(logItem?.id || row?.id ? { failureLogId: logItem?.id || row?.id } : {}),
            };
            const res = await apiService.retryBooking(payload);
            const resData = res?.data;
            const isSuccess =
                res?.status === 200 ||
                res?.status === 201 ||
                resData?.status === true ||
                resData?.responseCode === "200" ||
                resData?.responseCode === 200;

            if (isSuccess) {
                toast.success(resData?.message || resData?.responseMessage || "Booking retriggered successfully.");
            } else {
                toast.error(resData?.message || resData?.responseMessage || "Failed to retrigger booking.");
            }
            getTableList(labPartner, logDate, orderId);
        } catch (error) {
            console.error("Error retriggering booking:", error);
            const errMsg =
                error?.response?.data?.message ||
                error?.response?.data?.responseMessage ||
                error?.message ||
                "Failed to retrigger booking.";
            toast.error(errMsg);
        } finally {
            setRetryingOrderId(null);
        }
    };

    const handleNewCodeChange = (index, value) => {
        setDialogRows((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                newTestCode: value,
            };
            return next;
        });
    };

    const handleReasonChange = (index, value) => {
        setDialogRows((prev) => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                reason: value,
            };
            return next;
        });
    };

    const handleCloseDialog = () => {
        if (dialogSubmitting) return;
        setDialogOpen(false);
        setDialogData(null);
        setDialogRows([]);
        setDialogValidationErrors(false);
    };

    const handleConfirmRetryWithMapping = async () => {
        const hasEmptyCode = dialogRows.some((r) => !r.newTestCode || !r.newTestCode.trim());
        const hasEmptyReason = dialogRows.some((r) => !r.reason || !r.reason.trim());

        if (hasEmptyCode || hasEmptyReason) {
            setDialogValidationErrors(true);
            if (hasEmptyCode && hasEmptyReason) {
                toast.error("Please enter New Test Code and Reason for all unmapped tests.");
            } else if (hasEmptyCode) {
                toast.error("Please enter New Test Code for all unmapped tests.");
            } else {
                toast.error("Please enter a Reason for all unmapped tests.");
            }
            return;
        }

        setDialogSubmitting(true);
        try {
            const payload = {
                orderId: String(dialogData.orderId),
                triggeredBy: userCode,
                failureLogId: dialogData.failureLogId,
                testCodeUpdates: dialogRows.map((r) => ({
                    orderDetailId: r.orderDetailId,
                    codeType: r.codeType || "TEST",
                    oldTestCode: String(r.oldTestCode || ""),
                    newTestCode: String(r.newTestCode || "").trim(),
                    reason: String(r.reason || "").trim(),
                })),
            };

            const res = await apiService.retryBooking(payload);
            const resData = res?.data;
            const isSuccess =
                res?.status === 200 ||
                res?.status === 201 ||
                resData?.status === true ||
                resData?.responseCode === "200" ||
                resData?.responseCode === 200;

            if (isSuccess) {
                toast.success(resData?.message || resData?.responseMessage || "Booking retriggered successfully.");
                setDialogOpen(false);
                setDialogData(null);
                setDialogRows([]);
                getTableList(labPartner, logDate, orderId);
            } else {
                toast.error(resData?.message || resData?.responseMessage || "Failed to retrigger booking.");
            }
        } catch (error) {
            console.error("Error retrying booking with test code updates:", error);
            const errMsg =
                error?.response?.data?.message ||
                error?.response?.data?.responseMessage ||
                error?.message ||
                "Failed to retrigger booking.";
            toast.error(errMsg);
        } finally {
            setDialogSubmitting(false);
        }
    };

    const getCurrentPageItems = () => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return tableList.slice(startIndex, endIndex);
    };

    const currentPageItems = getCurrentPageItems();
    const currentPageIds = currentPageItems.map((r) => r.id);
    const allSelected = currentPageItems.length > 0 && currentPageIds.every(id => selectedIds.includes(id));
    const someSelected = selectedIds.length > 0 && !allSelected;

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        setSelectedIds([]);
    };

    const handleRowsPerPageChange = (event) => {
        const newSize = parseInt(event.target.value, 10);
        setRowsPerPage(newSize);
        setPage(0);
        setSelectedIds([]);
    };

    const fieldSx = {
        '& .MuiOutlinedInput-root': {
            height: 34, borderRadius: '7px', fontSize: '0.8125rem', bgcolor: '#fff',
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
        },
        '& .MuiInputLabel-root': { fontSize: '0.8125rem', top: '-2px', '&.MuiInputLabel-shrink': { top: 0 } },
        '& .MuiInputLabel-root.Mui-focused': { color: T.primary },
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', mt: 0, px: 0 }}>
            <Box
                sx={{
                    position: 'sticky',
                    top: 64,
                    zIndex: 10,
                    bgcolor: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    mb: 1,
                    pt: 0.5,
                    pb: 1,
                    borderBottom: `1px solid ${T.border}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    <Box sx={{
                        width: 32, height: 32, borderRadius: '8px',
                        background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: `0 2px 8px ${T.primaryMid}`,
                    }}>
                        <ReportProblemOutlinedIcon sx={{ color: '#fff', fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '15px', color: T.textPrimary, lineHeight: 1.2 }}>Lab Partner Error Log</Typography>
                        <Typography variant="caption" sx={{ color: T.textSecondary, fontSize: '11px' }}>Booking failures by lab partner, for one day at a time</Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <TextField
                        select size="small" label="Lab Partner" variant="outlined"
                        value={labPartner}
                        onChange={handleLabPartnerChange}
                        sx={{ minWidth: 175, ...fieldSx, '& .MuiSelect-select': { py: '4px !important', display: 'flex', alignItems: 'center' } }}
                    >
                        {labPartnerOptions.map((opt) => (
                            <MenuItem key={opt} value={opt} sx={{ fontSize: '0.8125rem' }}>{opt}</MenuItem>
                        ))}
                    </TextField>

                    <TextField
                        size="small" label="Date" type="date" variant="outlined"
                        value={logDate}
                        onChange={handleDateChange}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ max: todayStr() }}
                        sx={{ minWidth: 145, ...fieldSx }}
                    />

                    <TextField
                        size="small"
                        label="Order ID"
                        variant="outlined"
                        placeholder="Filter by Order ID"
                        value={orderId}
                        onChange={(e) => setOrderId(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                handleSearchOrderId();
                            }
                        }}
                        InputProps={{
                            endAdornment: orderId ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={handleClearOrderId}
                                        edge="end"
                                        sx={{ p: 0.5 }}
                                    >
                                        <CloseIcon sx={{ fontSize: 16, color: T.textSecondary }} />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                        sx={{ minWidth: 165, ...fieldSx }}
                    />

                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                        onClick={handleSearchOrderId}
                        disabled={loading}
                        sx={{
                            height: 34,
                            borderRadius: '7px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            px: 1.75,
                            bgcolor: T.primary,
                            boxShadow: 'none',
                            '&:hover': { bgcolor: T.primaryDark, boxShadow: 'none' },
                        }}
                    >
                        Search
                    </Button>

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: T.border }} />

                    <Tooltip title="Refresh">
                        <IconButton
                            onClick={handleRefresh}
                            disabled={loading}
                            sx={{
                                border: `1px solid ${T.border}`,
                                borderRadius: '7px',
                                width: 34,
                                height: 34,
                                color: T.textSecondary,
                                '&:hover': { borderColor: T.primary, color: T.primary, bgcolor: T.primaryLight },
                            }}
                        >
                            <RefreshIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {(loading || updating) && (
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.35)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <CircularProgress sx={{ color: T.primary }} />
                </Box>
            )}

            <Paper elevation={0} sx={{ borderRadius: '12px', border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: `1px solid ${T.border}`, bgcolor: T.sectionBg, flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant="caption" sx={{ color: T.textSecondary }}>
                        <strong>{tableList.length}</strong> failure{tableList.length !== 1 ? 's' : ''}
                    </Typography>
                </Box>

                <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)', minHeight: 350, overflow: 'auto' }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ background: `linear-gradient(90deg, ${T.primaryLight} 0%, #f8f9ff 100%)` }}>
                                {['Order ID', 'Failure Date', 'Order Status', 'Status', 'Failure Response', 'Remove'].map(col => (
                                    <TableCell key={col} sx={{ fontWeight: 700, fontSize: '12px', letterSpacing: '0.04em', textTransform: 'uppercase', color: T.primary, borderBottom: `2px solid ${T.primaryMid}`, py: 1.5, whiteSpace: 'nowrap', bgcolor: T.primaryLight }}>
                                        {col}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentPageItems.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: T.textSecondary }}>
                                            <ReportProblemOutlinedIcon sx={{ fontSize: 40, opacity: 0.25 }} />
                                            <Typography variant="body2">No failures found</Typography>
                                            <Typography variant="caption">
                                                {orderId ? `No failures found for Order ID "${orderId}"` : 'Try a different lab partner or date'}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                currentPageItems.map((row, index) => {
                                    const isFailure = (row.status || '').toUpperCase() === 'FAILURE';
                                    const isSelected = selectedIds.includes(row.id);
                                    const responseText = row.responseBody || '';
                                    const isLong = responseText.length > 60;
                                    return (
                                        <TableRow
                                            key={row.id}
                                            sx={{
                                                bgcolor: isSelected ? T.rowHover : (index % 2 === 0 ? '#fff' : T.rowAlt),
                                                '&:hover': { bgcolor: T.rowHover },
                                                '& td': { borderBottom: `1px solid ${T.border}`, py: 1.25, fontSize: '13px' },
                                            }}
                                        >
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '12px' }}>{row.orderId || '-'}</TableCell>
                                            <TableCell sx={{ color: T.textSecondary, fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDateTime(row.createdDate)}</TableCell>

                                            <TableCell sx={{ fontSize: '12px' }}>{row.orderStatus || '-'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={row.status || '-'}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '11px', fontWeight: 700, height: 22, borderRadius: '6px',
                                                        bgcolor: isFailure ? T.errorBg : T.successBg,
                                                        color: isFailure ? T.error : T.success,
                                                        border: `1px solid ${isFailure ? T.errorMid : '#a5d6a7'}`,
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ maxWidth: 280 }}>
                                                {isLong ? (
                                                    <Tooltip title={responseText} arrow placement="top">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'default' }}>
                                                            <Typography sx={{ fontSize: '12px', color: T.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 220 }}>
                                                                {responseText}
                                                            </Typography>
                                                            <InfoOutlinedIcon sx={{ fontSize: 14, color: T.textSecondary, flexShrink: 0 }} />
                                                        </Box>
                                                    </Tooltip>
                                                ) : (
                                                    <Typography sx={{ fontSize: '12px', color: T.textSecondary }}>{responseText || '-'}</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Tooltip title={row.orderId ? `Retrigger booking for Order ${row.orderId}` : 'No Order ID'}>
                                                        <span>
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                startIcon={retryingOrderId === row.orderId ? <CircularProgress size={13} color="inherit" /> : <ReplayIcon sx={{ fontSize: 15 }} />}
                                                                disabled={!row.orderId || retryingOrderId === row.orderId || updating}
                                                                onClick={() => handleRetrigger(row.orderId, row)}
                                                                sx={{
                                                                    textTransform: 'none',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    py: 0.35,
                                                                    px: 1.25,
                                                                    borderRadius: '6px',
                                                                    background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                                                                    color: '#fff',
                                                                    boxShadow: '0 1px 3px rgba(25, 118, 210, 0.25)',
                                                                    '&:hover': {
                                                                        background: `linear-gradient(135deg, ${T.primaryDark} 0%, #0d47a1 100%)`,
                                                                        boxShadow: '0 2px 6px rgba(25, 118, 210, 0.35)',
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        bgcolor: '#e2e8f0',
                                                                        color: '#94a3b8',
                                                                    },
                                                                }}
                                                            >
                                                                {retryingOrderId === row.orderId ? 'Retrying...' : 'Retrigger'}
                                                            </Button>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title={isFailure ? 'Mark as handled' : 'Already updated'}>
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={!isFailure || updating}
                                                                onClick={() => markHandled([row.id])}
                                                                sx={{
                                                                    color: isFailure ? T.primary : T.disabled,
                                                                    border: `1px solid ${isFailure ? T.primaryMid : T.border}`,
                                                                    borderRadius: '6px',
                                                                    p: '4px',
                                                                    '&:hover': isFailure ? { bgcolor: T.primaryLight, borderColor: T.primary } : {},
                                                                }}
                                                            >
                                                                <DoneIcon sx={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                
                <TablePagination
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    component="div"
                    count={tableList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    sx={{
                        borderTop: `1px solid ${T.border}`,
                        '& .MuiTablePagination-select': { fontSize: '13px' },
                        '& .MuiTablePagination-displayedRows': { fontSize: '13px' },
                    }}
                />
            </Paper>

            {/* Unmapped Test Mapping Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '14px',
                        overflow: 'hidden',
                        boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        p: 2,
                        px: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: `1px solid ${T.border}`,
                        bgcolor: T.sectionBg,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: '8px',
                                background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryDark} 100%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                            }}
                        >
                            <ReplayIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '16px', color: T.textPrimary }}>
                                    Map Unmapped Test Codes
                                </Typography>
                                {dialogData?.orderId && (
                                    <Chip
                                        size="small"
                                        label={`Order #${dialogData.orderId}`}
                                        sx={{
                                            height: 22,
                                            fontWeight: 700,
                                            fontSize: '11px',
                                            bgcolor: T.primaryLight,
                                            color: T.primaryDark,
                                            border: `1px solid ${T.primaryMid}`,
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography variant="caption" sx={{ color: T.textSecondary, fontSize: '11.5px' }}>
                                Provide the mapped test codes and reason to retry booking with the lab partner
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={handleCloseDialog}
                        disabled={dialogSubmitting}
                        sx={{ color: T.textSecondary }}
                    >
                        <CloseIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 2.5, pt: '20px !important' }}>
                    <Alert
                        severity="warning"
                        sx={{
                            mb: 2.5,
                            borderRadius: '8px',
                            fontSize: '12.5px',
                            '& .MuiAlert-icon': { fontSize: 20 },
                        }}
                    >
                        The lab partner reported unmapped tests for this order. Please enter the mandatory <strong>New Test Code</strong> and <strong>Reason</strong> for each test below before retriggering the booking.
                    </Alert>

                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: `1px solid ${T.border}`,
                            borderRadius: '10px',
                            maxHeight: 380,
                            overflow: 'auto',
                        }}
                    >
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ bgcolor: T.primaryLight }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: T.primaryDark, bgcolor: T.primaryLight, py: 1.25 }}>
                                        TEST NAME
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: T.primaryDark, bgcolor: T.primaryLight, py: 1.25, width: 170 }}>
                                        CURRENT TEST CODE
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: T.primaryDark, bgcolor: T.primaryLight, py: 1.25, width: 220 }}>
                                        NEW TEST CODE *
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '12px', color: T.primaryDark, bgcolor: T.primaryLight, py: 1.25, width: 250 }}>
                                        REASON *
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {dialogRows.map((row, idx) => {
                                    const isCodeError = dialogValidationErrors && (!row.newTestCode || !row.newTestCode.trim());
                                    const isReasonError = dialogValidationErrors && (!row.reason || !row.reason.trim());
                                    return (
                                        <TableRow
                                            key={row.tempId || idx}
                                            sx={{
                                                '&:hover': { bgcolor: T.rowHover },
                                                '& td': { py: 1.5, verticalAlign: 'top', borderBottom: `1px solid ${T.border}` },
                                            }}
                                        >
                                            <TableCell>
                                                <Box sx={{ pt: 0.5 }}>
                                                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: T.textPrimary }}>
                                                        {row.testName}
                                                    </Typography>
                                                    {row.codeType && (
                                                        <Chip
                                                            size="small"
                                                            label={row.codeType}
                                                            sx={{
                                                                height: 18,
                                                                fontSize: '10px',
                                                                fontWeight: 700,
                                                                bgcolor: '#F1F5F9',
                                                                color: '#475569',
                                                                borderRadius: '4px',
                                                                mt: 0.5,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ pt: 0.5 }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontSize: '12.5px',
                                                            fontWeight: 600,
                                                            color: T.textSecondary,
                                                            bgcolor: '#F8FAFC',
                                                            px: 1,
                                                            py: 0.35,
                                                            borderRadius: '6px',
                                                            display: 'inline-block',
                                                            border: `1px solid ${T.border}`,
                                                        }}
                                                    >
                                                        {row.currentTestCode || row.oldTestCode || '-'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    placeholder="Enter new test code..."
                                                    value={row.newTestCode}
                                                    onChange={(e) => handleNewCodeChange(idx, e.target.value)}
                                                    error={isCodeError}
                                                    helperText={isCodeError ? 'New test code is required' : ''}
                                                    autoFocus={idx === 0}
                                                    fullWidth
                                                    disabled={dialogSubmitting}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            height: 36,
                                                            borderRadius: '7px',
                                                            fontSize: '12.5px',
                                                            bgcolor: '#fff',
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
                                                        },
                                                        '& .MuiFormHelperText-root': {
                                                            fontSize: '10.5px',
                                                            mt: 0.25,
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size="small"
                                                    multiline
                                                    minRows={2}
                                                    maxRows={4}
                                                    placeholder="Enter reason..."
                                                    value={row.reason}
                                                    onChange={(e) => handleReasonChange(idx, e.target.value)}
                                                    error={isReasonError}
                                                    helperText={isReasonError ? 'Reason is required' : ''}
                                                    fullWidth
                                                    disabled={dialogSubmitting}
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            borderRadius: '7px',
                                                            fontSize: '12.5px',
                                                            bgcolor: '#fff',
                                                            p: 1,
                                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: T.primary },
                                                        },
                                                        '& .MuiFormHelperText-root': {
                                                            fontSize: '10.5px',
                                                            mt: 0.25,
                                                        },
                                                    }}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 2,
                        px: 2.5,
                        borderTop: `1px solid ${T.border}`,
                        bgcolor: T.sectionBg,
                        gap: 1.5,
                    }}
                >
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCloseDialog}
                        disabled={dialogSubmitting}
                        sx={{
                            borderRadius: '7px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '12.5px',
                            borderColor: T.border,
                            color: T.textSecondary,
                            px: 2,
                            '&:hover': {
                                borderColor: T.textSecondary,
                                bgcolor: '#f1f5f9',
                            },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={handleConfirmRetryWithMapping}
                        disabled={dialogSubmitting}
                        startIcon={dialogSubmitting ? <CircularProgress size={14} color="inherit" /> : <ReplayIcon sx={{ fontSize: 16 }} />}
                        sx={{
                            borderRadius: '7px',
                            textTransform: 'none',
                            fontWeight: 700,
                            fontSize: '12.5px',
                            px: 2.5,
                            py: 0.65,
                            bgcolor: T.primary,
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: T.primaryDark,
                                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.35)',
                            },
                        }}
                    >
                        {dialogSubmitting ? 'Retrying Booking...' : 'Retry Booking'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default LabPartnerErrorLogList;